import { useEffect, useMemo, useRef, useState } from "react";

import { Badge } from "@/components/Badge";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { useApps, useCustomProductPages } from "@/lib/api-hooks";
import { useCanWriteApp } from "@/lib/useAuth";

import { CppColumnsControl } from "./ColumnsControl";
import { CPP_DEFAULT_COLS } from "./columns";
import { GroupBlock } from "./Results";
import { CppWizard } from "./CppWizard";
import {
  useApplyCppWinner,
  useCppColumnPresets,
  useCppTestDetail,
  useCppTests,
  usePauseCppTest,
  useResumeCppTest,
  useStopCppTest,
} from "./hooks";
import { CPP_METRIC_META } from "./types";
import type { CppTestListItem } from "./types";

function daysSince(iso: string | null): number {
  if (!iso) return 0;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

function DecisionMini({ test }: { test: CppTestListItem }) {
  const decided = test.decided_count;
  const total = Math.max(test.group_count, 1);
  const allWin = decided === total && decided > 0;
  const pct = Math.max(6, Math.min(100, (decided / total) * 100 || 10));
  const cls = allWin ? "win" : decided > 0 ? "part" : "wait";
  return (
    <div className="cpp-mini">
      <div className={"cpp-mini-bar " + cls}>
        <span style={{ width: pct + "%" }} />
      </div>
      <div className="cpp-mini-lbl">
        {allWin ? (
          <span className="pos">
            {I.check} {total > 1 ? "All groups decided" : "Winner found"}
          </span>
        ) : decided > 0 ? (
          <span>
            {decided}/{total} groups decided
          </span>
        ) : (
          <span className="muted">Collecting data</span>
        )}
      </div>
    </div>
  );
}

function useScreenshotMap(asaAppId: number | null) {
  const cpps = useCustomProductPages(asaAppId);
  return useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of cpps.data?.rows || []) {
      m.set(r.product_page_id ?? "default", r.screenshots || []);
    }
    return m;
  }, [cpps.data]);
}

function ActiveRow({
  test,
  canEdit,
  apps,
}: {
  test: CppTestListItem;
  canEdit: boolean;
  apps: ReturnType<typeof useApps>;
}) {
  const [open, setOpen] = useState(false);
  const [cols, setCols] = useState<string[]>(CPP_DEFAULT_COLS);
  const colPresets = useCppColumnPresets(open ? test.app_id : null);
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultRef.current) return;
    const def = colPresets.data?.find((p) => p.is_default);
    if (def) {
      setCols(def.columns);
      appliedDefaultRef.current = true;
    }
  }, [colPresets.data]);
  const detail = useCppTestDetail(open ? test.id : null);
  const asaAppId = apps.data?.find((a) => a.id === test.app_id)?.asa_app_id ?? null;
  const screenshotMap = useScreenshotMap(open ? asaAppId : null);
  const pauseMut = usePauseCppTest();
  const resumeMut = useResumeCppTest();
  const stopMut = useStopCppTest();
  const applyMut = useApplyCppWinner();
  // Apply used to be fire-and-forget: no success or error feedback, so a click
  // that succeeded, failed, or hit a done group all looked identical ("nothing
  // happens"). Surface every outcome.
  const [applyMsg, setApplyMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const flash = (ok: boolean, text: string) => {
    setApplyMsg({ ok, text });
    window.setTimeout(() => setApplyMsg(null), 4000);
  };
  const m = CPP_METRIC_META[test.metric];

  const splitLabel = detail.data?.groups[0]?.variants
    .map((v) => Math.round(v.target_split_pct))
    .join(" / ");

  return (
    <div className={"cpp-trow-wrap" + (open ? " open" : "")}>
      <button className="cpp-trow" onClick={() => setOpen((o) => !o)}>
        <span className={"twist" + (open ? " open" : "")}>{I.chevron}</span>
        <span className="cpp-trow-main">
          <span className="cpp-trow-name">
            {test.name}
            {test.decided_count > 0 && (
              <span className="cpp-win-chip">{I.check} Winner</span>
            )}
          </span>
          <span className="cpp-trow-sub">
            {test.app_name || "App"} · Campaign #{test.campaign_id}
          </span>
        </span>
        <span className="cpp-trow-col">
          <span className="cpp-col-k">Scope</span>
          <span className="cpp-col-v">
            {test.group_count} group{test.group_count > 1 ? "s" : ""} · {test.variant_count} pages
          </span>
        </span>
        <span className="cpp-trow-col">
          <span className="cpp-col-k">Metric</span>
          <span className="cpp-col-v">{m.short}</span>
        </span>
        <span className="cpp-trow-col">
          <span className="cpp-col-k">Status</span>
          <span className="cpp-col-v">
            {test.status === "paused" ? "Paused" : `Running ${daysSince(test.started_at)}d`}
          </span>
        </span>
        <span className="cpp-trow-col wide">
          <span className="cpp-col-k">Progress</span>
          <DecisionMini test={test} />
        </span>
      </button>

      {open && (
        <div className="cpp-trow-body">
          <div className="cpp-settings-bar">
            <div className="cpp-set">
              <span className="cpp-set-k">Metric</span>
              <span className="cpp-set-v">{m.label}</span>
            </div>
            {detail.data && (
              <>
                <div className="cpp-set">
                  <span className="cpp-set-k">Confidence</span>
                  <span className="cpp-set-v">{detail.data.confidence_level}%</span>
                </div>
                <div className="cpp-set">
                  <span className="cpp-set-k">Min. improvement</span>
                  <span className="cpp-set-v">{detail.data.min_uplift_pct}%</span>
                </div>
                {splitLabel && (
                  <div className="cpp-set">
                    <span className="cpp-set-k">Split</span>
                    <span className="cpp-set-v">{splitLabel}%</span>
                  </div>
                )}
                <div className="cpp-set">
                  <span className="cpp-set-k">Auto-replace</span>
                  <span className="cpp-set-v">{detail.data.auto_apply ? "On" : "Off"}</span>
                </div>
              </>
            )}
            <div className="cpp-set-spacer" />
            <CppColumnsControl appId={test.app_id} value={cols} onChange={setCols} />
            {canEdit && test.status === "running" && (
              <button className="btn sm" onClick={() => pauseMut.mutate(test.id)} disabled={pauseMut.isPending}>
                {I.pause} Pause
              </button>
            )}
            {canEdit && test.status === "paused" && (
              <button className="btn sm" onClick={() => resumeMut.mutate(test.id)} disabled={resumeMut.isPending}>
                {I.play} Resume
              </button>
            )}
            {canEdit && (
              <button
                className="btn sm danger"
                onClick={() => {
                  if (window.confirm("Stop this test? Rotation ends and the current pages stay live."))
                    stopMut.mutate(test.id);
                }}
                disabled={stopMut.isPending}
              >
                Stop
              </button>
            )}
          </div>
          {detail.isLoading && <div className="muted" style={{ padding: 16 }}>Loading results…</div>}
          {detail.data?.groups.map((g) => (
            <GroupBlock
              key={g.group_id}
              test={detail.data!}
              group={g}
              // A done group already has its winner applied and serving — show
              // the "Applied ✓" state, not an active "Apply winner" button the
              // user clicks to no effect.
              frozen={g.status === "done"}
              canEdit={canEdit}
              cols={cols}
              screenshotsByPpid={screenshotMap}
              onApply={(variantId) =>
                applyMut.mutate(
                  { testId: test.id, groupId: g.group_id, variantId },
                  {
                    onSuccess: () => flash(true, "Winner applied — the ad group now serves it."),
                    onError: () =>
                      flash(false, "Couldn't apply — you may not have edit access to this app."),
                  },
                )
              }
            />
          ))}
          {applyMsg && (
            <div className={"cpp-toast" + (applyMsg.ok ? "" : " err")}>
              {applyMsg.ok ? I.check : "⚠"} {applyMsg.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryRow({
  test,
  apps,
}: {
  test: CppTestListItem;
  apps: ReturnType<typeof useApps>;
}) {
  const [open, setOpen] = useState(false);
  const [cols, setCols] = useState<string[]>(CPP_DEFAULT_COLS);
  const colPresets = useCppColumnPresets(open ? test.app_id : null);
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultRef.current) return;
    const def = colPresets.data?.find((p) => p.is_default);
    if (def) {
      setCols(def.columns);
      appliedDefaultRef.current = true;
    }
  }, [colPresets.data]);
  const detail = useCppTestDetail(open ? test.id : null);
  const asaAppId = apps.data?.find((a) => a.id === test.app_id)?.asa_app_id ?? null;
  const screenshotMap = useScreenshotMap(open ? asaAppId : null);
  const m = CPP_METRIC_META[test.metric];
  const outcome =
    test.decided_count > 0
      ? { cls: "enabled" as const, label: "Winner found" }
      : { cls: "paused" as const, label: "No significant difference" };

  return (
    <div className={"cpp-trow-wrap" + (open ? " open" : "")}>
      <button className="cpp-trow" onClick={() => setOpen((o) => !o)}>
        <span className={"twist" + (open ? " open" : "")}>{I.chevron}</span>
        <span className="cpp-trow-main">
          <span className="cpp-trow-name">{test.name}</span>
          <span className="cpp-trow-sub">
            {test.app_name || "App"} · Campaign #{test.campaign_id}
          </span>
        </span>
        <span className="cpp-trow-col wide">
          <span className="cpp-col-k">Period</span>
          <span className="cpp-col-v">{daysSince(test.started_at)}d</span>
        </span>
        <span className="cpp-trow-col">
          <span className="cpp-col-k">Metric</span>
          <span className="cpp-col-v">{m.short}</span>
        </span>
        <span className="cpp-trow-col wider">
          <span className="cpp-col-k">Outcome</span>
          <span className="cpp-col-v">
            <Badge kind={outcome.cls}>
              {test.decided_count > 0 && I.check}
              {outcome.label}
            </Badge>
          </span>
        </span>
      </button>
      {open && (
        <div className="cpp-trow-body">
          <div className="cpp-settings-bar">
            {detail.data && (
              <>
                <div className="cpp-set">
                  <span className="cpp-set-k">Metric</span>
                  <span className="cpp-set-v">{m.label}</span>
                </div>
                <div className="cpp-set">
                  <span className="cpp-set-k">Confidence</span>
                  <span className="cpp-set-v">{detail.data.confidence_level}%</span>
                </div>
                <div className="cpp-set">
                  <span className="cpp-set-k">Min. improvement</span>
                  <span className="cpp-set-v">{detail.data.min_uplift_pct}%</span>
                </div>
              </>
            )}
            <div className="cpp-set-spacer" />
            <CppColumnsControl appId={test.app_id} value={cols} onChange={setCols} />
            <Badge kind="paused">{I.clock} Frozen · final figures</Badge>
          </div>
          {detail.isLoading && <div className="muted" style={{ padding: 16 }}>Loading…</div>}
          {detail.data?.groups.map((g) => (
            <GroupBlock
              key={g.group_id}
              test={detail.data!}
              group={g}
              frozen
              canEdit={false}
              cols={cols}
              screenshotsByPpid={screenshotMap}
              onApply={() => {}}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CppEmpty({ kind, onCreate }: { kind: "active" | "history"; onCreate: () => void }) {
  if (kind === "active")
    return (
      <div className="cpp-empty">
        <div className="cpp-empty-art">
          {["#cfcbff", "#e6e4ff", "#cfcbff"].map((c, i) => (
            <span key={i} className="cpp-thumb" style={{ "--tc": c } as React.CSSProperties}>
              <span className="cpp-thumb-notch" />
            </span>
          ))}
        </div>
        <h3>No active tests</h3>
        <p>Start a test to compare Custom Product Pages and let oneads call the winner.</p>
        <button className="btn primary" onClick={onCreate}>
          {I.plus} Create new test
        </button>
      </div>
    );
  return (
    <div className="cpp-empty">
      <div className="cpp-empty-art">
        <span className="cpp-empty-ico">{I.clock}</span>
      </div>
      <h3>No completed tests yet</h3>
      <p>Finished tests will be archived here with their final figures and winners.</p>
    </div>
  );
}

export function TesterPage() {
  const apps = useApps();
  const [tab, setTab] = useState<"active" | "history">("active");
  const [wizard, setWizard] = useState(false);
  const [toast, setToast] = useState(false);
  const [appFilter, setAppFilter] = useState<number | "all">("all");
  // Per-app edit gate (§51): writable for the filtered app the user can write
  // (or any-app when "All" — backend enforces the test's app on create/apply).
  const canEdit = useCanWriteApp(
    typeof appFilter === "number" ? appFilter : null,
  );

  const active = useCppTests("active");
  const history = useCppTests("history");

  const filterByApp = (rows: CppTestListItem[] | undefined) =>
    (rows || []).filter((t) => appFilter === "all" || t.app_id === appFilter);
  const activeRows = filterByApp(active.data);
  const historyRows = filterByApp(history.data);

  if (wizard) {
    return (
      <div className="content">
        <CppWizard
          onClose={() => setWizard(false)}
          onLaunch={() => {
            setWizard(false);
            setTab("active");
            setToast(true);
            window.setTimeout(() => setToast(false), 3500);
          }}
        />
      </div>
    );
  }

  return (
    <div className="content">
      {toast && <div className="cpp-toast">{I.check} Test launched — provisioning ads & collecting data.</div>}

      <div className="cpp-apphead">
        <select
          className="select"
          value={appFilter}
          onChange={(e) =>
            setAppFilter(e.target.value === "all" ? "all" : Number(e.target.value))
          }
          style={{ maxWidth: 260 }}
        >
          <option value="all">All apps</option>
          {(apps.data || []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <span className="cpp-apphead-hint">Tests run per app — filter to see one app&apos;s CPP tests.</span>
      </div>

      <div className="page-head">
        <div>
          <h1 className="page-title">
            <em>CPP</em> tester
          </h1>
          <div className="page-sub">
            Compare Custom Product Pages head-to-head per ad-group. See the key numbers at a glance
            and roll out the winner.
          </div>
        </div>
        {canEdit && (
          <Btn variant="primary" icon={I.plus} onClick={() => setWizard(true)}>
            Create new test
          </Btn>
        )}
      </div>

      <div className="cpp-tabbar">
        <div className="chip-group">
          <button className={tab === "active" ? "active" : ""} onClick={() => setTab("active")}>
            Active tests<span className="cpp-tab-n">{activeRows.length}</span>
          </button>
          <button className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>
            Test history<span className="cpp-tab-n">{historyRows.length}</span>
          </button>
        </div>
      </div>

      {tab === "active" &&
        (active.isLoading ? (
          <div className="muted" style={{ padding: 24 }}>Loading tests…</div>
        ) : activeRows.length === 0 ? (
          <CppEmpty kind="active" onCreate={() => setWizard(true)} />
        ) : (
          <div className="cppt-list">
            {activeRows.map((t) => (
              <ActiveRow key={t.id} test={t} canEdit={canEdit} apps={apps} />
            ))}
          </div>
        ))}
      {tab === "history" &&
        (history.isLoading ? (
          <div className="muted" style={{ padding: 24 }}>Loading…</div>
        ) : historyRows.length === 0 ? (
          <CppEmpty kind="history" onCreate={() => setWizard(true)} />
        ) : (
          <div className="cppt-list">
            {historyRows.map((t) => (
              <HistoryRow key={t.id} test={t} apps={apps} />
            ))}
          </div>
        ))}
    </div>
  );
}
